import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Fragment, Component } from 'react';
import styled from 'styled-components';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import UITourStep from 'ui-shepherd-react/tour/UITourStep';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import { SLINKY } from 'HubStyleTokens/colors';
import OnboardingTourButtons from './OnboardingTourButtons';
import OnboardingTourFinishModal from './modals/OnboardingTourFinishModal';
import OnboardingTourCloseModal from './modals/OnboardingTourCloseModal';
import OnboardingTourImageModal from './modals/OnboardingTourImageModal';
import OnboardingTourModal from './modals/OnboardingTourModal';
import OnboardingTourShowMeHowText from './OnboardingTourShowMeHowText';
import { DISABLE_MODAL_BLUR_CLASS, RemoveModalFullscreenBlur } from './modals/RemoveModalFullscreenBlur';
import { TOUR_ID, ONBOARDING_TOUR_ID } from '../constants/URL';
import { getAttachToElementQuery } from '../util/attachToUtil';
import { getElement } from '../util/elementUtil';
import { beforeStepStartListener, attachEscapeHatchListeners } from '../util/tourListener';
import { removeQueryParams } from '../util/urlUtils';
import { MESSAGE_KEY, MESSAGE_ACTIONS } from '../constants/TourMessageConstants';
import { DONE_STEP, FINISH } from '../constants/TourActions';
import { isBannerEnabled } from '../util/banner';
import { addOnboardingTourBodyClass, removeOnboardingTourBodyClass } from '../util/body';
import { Metrics } from '../util/metrics';
import StyledConfetti from './Confetti';
const StyledTourStep = styled(UITourStep).withConfig({
  displayName: "OnboardingTourSteps__StyledTourStep",
  componentId: "sc-11k1bwu-0"
})(["header{padding:12px 24px;}"]);
const ConstrainedStepBody = styled.div.withConfig({
  displayName: "OnboardingTourSteps__ConstrainedStepBody",
  componentId: "sc-11k1bwu-1"
})(["ul{margin-bottom:0;padding-left:20px;}"]);
const StepText = styled.div.withConfig({
  displayName: "OnboardingTourSteps__StepText",
  componentId: "sc-11k1bwu-2"
})(["font-size:12px;color:", ";font-weight:400;"], SLINKY);

class OnboardingTourSteps extends Component {
  // TODO: uncomment this when `allowDeclareFields` is enabled
  // https://git.hubteam.com/HubSpot/asset-bender-hubspot/pull/3245
  // declare context: OnboardingTours.TourContext;
  constructor(props) {
    super(props);
    this.unlisteners = [];
    this.removeTourStepsListeners = this.removeTourListeners.bind(this, 'tourStep');
    this.state = {
      shouldShowCloseModal: false,
      stepsWithCounter: this.getStepsWithCounter(),
      showCompletionModal: false
    };
    this.beforeStart = this.beforeStart.bind(this);
    this.getAttachToElement = this.getAttachToElement.bind(this);
    this.onOnboardingTourMessage = this.onOnboardingTourMessage.bind(this);
    this.handleTourCompletion = this.handleTourCompletion.bind(this);
    this.addTourUnlistener = this.addTourUnlistener.bind(this);
    this.removeTourListeners = this.removeTourListeners.bind(this);
    this.getContent = this.getContent.bind(this);
  }

  componentDidMount() {
    this.handleTourCompletion();
    this.startTour();
    window.addEventListener('message', this.onOnboardingTourMessage);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tourData !== this.props.tourData) {
      this.setState({
        stepsWithCounter: this.getStepsWithCounter()
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onOnboardingTourMessage);

    if (this.removeCompletionSubscription) {
      this.removeCompletionSubscription();
    }
  }

  handleTourCompletion() {
    const {
      tourData,
      tourId
    } = this.props; // TODO: remove type assertion when above `declare context` is uncommented

    const {
      tour
    } = this.context;
    const shouldShowCompleteModal = !isBannerEnabled() && tourData.enableTourCompleteModal && !tourData.returnUrl;
    const [completionSubscription] = tour.subscribe(stepId => {
      if (stepId === DONE_STEP) {
        if (shouldShowCompleteModal) {
          this.setState({
            showCompletionModal: true
          });
        }

        if (tourData.removeTourQueryParamOnCompletion) {
          removeQueryParams([ONBOARDING_TOUR_ID, TOUR_ID]);
        }

        window.postMessage({
          key: MESSAGE_KEY,
          action: MESSAGE_ACTIONS.TOUR_COMPLETED,
          tourId
        }, '*');
        this.removeTourListeners();
        removeOnboardingTourBodyClass();
        Metrics.counter('complete-onboarding-tour', {
          tourId,
          owningTeam: tourData.owningTeam
        }).increment();
      }
    });
    this.removeCompletionSubscription = completionSubscription;
  }

  onOnboardingTourMessage(event) {
    const {
      action,
      key
    } = event.data;
    const {
      tour
    } = this.context;

    if (key !== MESSAGE_KEY || !tour) {
      return;
    }

    if (action === MESSAGE_ACTIONS.CLOSE) {
      this.removeTourListeners();
      tour.deactivate();
    }
  }

  beforeStart() {
    const {
      tour
    } = this.context;
    const {
      prevTourLength,
      linkedToursTotalStepCount,
      returnUrl
    } = this.props.tourData;
    const {
      stepsWithCounter
    } = this.state;
    const currentStepConfig = this.getCurrentStepConfig();
    const {
      beforeStart
    } = currentStepConfig; // Listeners from other (previous/next) steps which have not been fired should be removed

    this.removeTourStepsListeners(); // The step will execute and wait for both of these to resolve before starting.

    return Promise.all([typeof beforeStart === 'function' && beforeStart(this.context), beforeStepStartListener(tour, currentStepConfig, this.addTourUnlistener, prevTourLength + stepsWithCounter.length, linkedToursTotalStepCount, returnUrl)]);
  }

  startTour() {
    const {
      returnUrl
    } = this.props.tourData;
    const {
      tourId,
      tourData: {
        prevTourLength,
        linkedToursTotalStepCount,
        owningTeam
      }
    } = this.props;
    const {
      stepsWithCounter
    } = this.state;
    const {
      tour
    } = this.context;
    attachEscapeHatchListeners(tour, this.addTourUnlistener, this.removeTourListeners, prevTourLength + stepsWithCounter.length, linkedToursTotalStepCount, returnUrl);
    addOnboardingTourBodyClass();
    tour.start(tourId);
    Metrics.counter('start-onboarding-tour', {
      tourId: this.props.tourId,
      owningTeam
    }).increment();
  }

  getSteps(filterFn) {
    const {
      steps
    } = this.props.tourData;
    return (filterFn ? steps.filter(filterFn) : steps).map(step => step.id);
  }

  getStepsWithCounter() {
    return this.getSteps(step => !step.isModal);
  }

  getStepProgress(stepKey, allStepKeys) {
    const {
      prevTourLength,
      linkedToursTotalStepCount
    } = this.props.tourData;
    const currentStepIndex = allStepKeys.indexOf(stepKey);
    const stepIndex = linkedToursTotalStepCount ? prevTourLength + currentStepIndex : currentStepIndex;
    const totalSteps = linkedToursTotalStepCount || allStepKeys.length;
    return {
      stepIndex,
      totalSteps
    };
  }

  getStepText(stepKey) {
    const {
      stepIndex,
      totalSteps
    } = this.getStepProgress(stepKey, this.state.stepsWithCounter);
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: "onboarding-tours-common.stepProgress",
      options: {
        currentStep: stepIndex + 1,
        totalSteps
      }
    });
  }

  getIsLastStep(stepKey) {
    const {
      stepIndex,
      totalSteps
    } = this.getStepProgress(stepKey, this.getSteps());
    return stepIndex === totalSteps - 1;
  }

  getContent(step) {
    const {
      returnUrl
    } = this.props.tourData;
    const {
      buttons,
      showSteps = true,
      id
    } = step;

    if (this.getIsLastStep(id) && returnUrl) {
      // If returnUrl exists, redirect user to returnUrl and disable completion modal on tour finish button clicking
      const finishButton = (buttons || []).find(button => button.action === FINISH || button.commonText === 'endTour');

      if (finishButton) {
        finishButton.url = returnUrl;
        finishButton.disableTaskCompletionModal = true;
      } // If returnUrl exists, redirect user to returnUrl instead of User Guide on last step


      const userGuideButton = (buttons || []).find(button => button.url && button.url.startsWith('/user-guide/'));

      if (userGuideButton) {
        userGuideButton.url = returnUrl;
      } // If returnUrl exists and button link is another tour, append returnUrl to tour link


      const otherTourButton = (buttons || []).find(button => button.url && button.url.includes('tourId=') && !button.url.includes('returnUrl'));

      if (otherTourButton) {
        otherTourButton.url = `${otherTourButton.url}&returnUrl=${returnUrl}`;
      }
    }

    if (step.attachTo) {
      const {
        actionText,
        beforeBackClickHandler,
        enableBackButton,
        text,
        title,
        image,
        buttonSize,
        externalStepComponent
      } = step; // see if we can find a selector that matches on the page

      const textSelector = Object.entries(step.textSelectors || []).find(([selector]) => {
        return document.querySelector(selector);
      }); // if we found a selector to match one of the dynamic ones, use it
      // otherwise use the normal text value

      const textToShow = textSelector ? textSelector[1] : text;
      return {
        header: title && /*#__PURE__*/_jsx("h5", {
          children: title
        }),
        body: /*#__PURE__*/_jsxs(Fragment, {
          children: [showSteps && /*#__PURE__*/_jsx(StepText, {
            className: "m-bottom-1",
            children: this.getStepText(id)
          }), image && /*#__PURE__*/_jsx(UIFlex, {
            justify: "center",
            children: /*#__PURE__*/_jsx(UIImage, {
              alt: image.alt,
              className: "m-bottom-4",
              src: image.src,
              width: image.width
            })
          }), textToShow && /*#__PURE__*/_jsx(ConstrainedStepBody, {
            children: textToShow
          }), actionText && /*#__PURE__*/_jsx("div", {
            className: "p-top-4",
            children: /*#__PURE__*/_jsx("strong", {
              children: actionText
            })
          }), externalStepComponent && /*#__PURE__*/_jsx("div", {
            className: "p-top-4",
            children: externalStepComponent
          }), /*#__PURE__*/_jsx(OnboardingTourShowMeHowText, {
            className: "p-top-4",
            stepId: id,
            tourData: this.props.tourData,
            tourId: this.props.tourId
          })]
        }),
        footer: buttons && buttons.length > 0 && /*#__PURE__*/_jsx(OnboardingTourButtons, {
          beforeBackClickHandler: beforeBackClickHandler,
          buttonSize: buttonSize || 'sm',
          buttons: buttons,
          completeActionKey: this.props.tourData.completeActionKey,
          enableBackButton: enableBackButton
        })
      };
    }

    const TourModal = step.image ? OnboardingTourImageModal : OnboardingTourModal;
    return /*#__PURE__*/_jsx(TourModal, {
      "data-test-step-index": step.id,
      onCloseComplete: () => {
        const {
          blurBackground
        } = this.getModalConfig(); // When the modal of previous step is closed, enable the current modal's blur

        if (blurBackground !== false) {
          document.body.classList.remove(DISABLE_MODAL_BLUR_CLASS);
        }
      },
      onOpenStart: () => {
        const {
          blurBackground
        } = this.getModalConfig(); // Disable/Enable the current modal's blur

        if (blurBackground === false) {
          document.body.classList.add(DISABLE_MODAL_BLUR_CLASS);
        } else {
          document.body.classList.remove(DISABLE_MODAL_BLUR_CLASS);
        }
      },
      step: step
    });
  }

  getCurrentStepConfig() {
    const {
      tour
    } = this.context;
    const currentStepId = tour.getStep();
    return this.getStepConfig(currentStepId);
  }

  getStepConfig(stepId) {
    const {
      steps
    } = this.props.tourData;
    const currentStepConfig = steps.find(step => step.id === stepId);
    return currentStepConfig;
  }

  getAttachToElement() {
    const currentStepConfig = this.getCurrentStepConfig();

    if (!currentStepConfig) {
      return null;
    }

    const {
      attachTo
    } = currentStepConfig;
    const attachToElementQuery = getAttachToElementQuery(attachTo);
    const attachToElement = getElement({
      contains: attachTo && attachTo.contains,
      elementGetter: attachTo && attachTo.elementGetter,
      elementQuery: attachToElementQuery
    }); // For elements that don't load straight away, we need to pass
    // the element query which will be retried on load of step.

    return attachToElement || null;
  }

  addTourUnlistener(listener) {
    this.unlisteners.push(listener);
  }
  /**
   * Removes all event listeners of type `listenerType` from tour steps or escape hatches
   * (or all listeners if no `listenerType` argument is passed)
   */


  removeTourListeners(listenerType) {
    if (this.unlisteners.length === 0) {
      return;
    }

    const unlistenersToKeep = [];
    const tourStepsUnlisteners = !listenerType ? this.unlisteners : this.unlisteners.filter(unlistener => {
      const isTourStep = unlistener.listenerType === listenerType;

      if (!isTourStep) {
        unlistenersToKeep.push(unlistener);
      }

      return isTourStep;
    });
    tourStepsUnlisteners.forEach(({
      element,
      eventType,
      listener
    }) => {
      element.removeEventListener(eventType, listener);
    });
    this.unlisteners = unlistenersToKeep;
  }
  /**
   * Removes all of the listeners from tour steps
   */


  closeTourStep() {
    const {
      tourId
    } = this.props;
    removeOnboardingTourBodyClass();
    removeQueryParams([ONBOARDING_TOUR_ID, TOUR_ID]); // Triggers closing tour event

    window.postMessage({
      key: MESSAGE_KEY,
      action: MESSAGE_ACTIONS.CLOSE,
      tourId
    }, '*');
    const {
      returnUrl
    } = this.props.tourData;

    if (returnUrl) {
      window.location.href = returnUrl;
    }
  }

  getModalConfig() {
    const {
      modal
    } = this.getCurrentStepConfig() || {};
    return modal !== undefined && typeof modal !== 'boolean' ? modal : {};
  }

  renderActiveStep() {
    // Loop through the tour data, creating each step
    const currentStepConfig = this.getCurrentStepConfig();

    if (!currentStepConfig) {
      return null;
    }

    const {
      arrowSize,
      attachTo,
      id,
      onOpenComplete,
      pulser,
      showCloseButton = true,
      showExitModal = true,
      toggleOnTargetVisibilityChange,
      width,
      zIndex,
      showConfetti
    } = currentStepConfig; // Changes the width and z-index of the tour step if values are provided in the step config

    const tourStepProps = Object.assign({}, width && {
      width
    }, {}, zIndex && {
      zIndex
    });
    const scrollIntoView = attachTo && attachTo.scrollIntoView;
    const scrollTo = scrollIntoView === true ? 'nearest' : scrollIntoView || undefined;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [showConfetti && /*#__PURE__*/_jsx(StyledConfetti, {
        fixedPosition: true
      }), /*#__PURE__*/_jsx(StyledTourStep, Object.assign({
        arrowSize: arrowSize // Use function attachTo for:
        //   - get latest element after waitForElement observer and beforeStart callback
        //   - avoid passing incorrect element if waitForElement is not returned or page will be re-rendered
        //   - OnboardingTourSteps can't change attachTo dynamically after mount
        ,
        attachTo: attachTo ? this.getAttachToElement : undefined,
        beforeStart: this.beforeStart,
        content: this.getContent(currentStepConfig),
        "data-test-step-index": id,
        onClickCloseButton: showCloseButton ? () => {
          if (showExitModal === true) {
            this.setState({
              shouldShowCloseModal: true
            });
            return false; // Keep popover open
          }

          return this.closeTourStep();
        } : undefined,
        onOpenComplete: onOpenComplete,
        placement: attachTo && attachTo.on,
        pulser: pulser,
        scrollTo: scrollTo,
        showCloseButton: showCloseButton,
        stepKey: id,
        toggleOnTargetVisibilityChange: toggleOnTargetVisibilityChange
      }, tourStepProps), id), this.state.shouldShowCloseModal && /*#__PURE__*/_jsx(OnboardingTourCloseModal, {
        onConfirm: () => {
          this.setState({
            shouldShowCloseModal: false
          });
          this.closeTourStep();
        },
        onReject: () => {
          this.setState({
            shouldShowCloseModal: false
          });
        },
        stepId: id,
        tourId: this.props.tourId
      }), /*#__PURE__*/_jsx(RemoveModalFullscreenBlur, {})]
    });
  }

  render() {
    const {
      tourId,
      tourData
    } = this.props;

    if (this.state.showCompletionModal) {
      return /*#__PURE__*/_jsx(OnboardingTourFinishModal, {
        "data-test-step-index": "finish-modal",
        onClose: () => this.setState({
          showCompletionModal: false
        }),
        tourData: tourData,
        tourId: tourId
      });
    }

    return /*#__PURE__*/_jsx("span", {
      children: this.renderActiveStep()
    });
  }

}

OnboardingTourSteps.contextType = TourContext;
OnboardingTourSteps.propTypes = {
  tourData: PropTypes.object.isRequired,
  tourId: PropTypes.string.isRequired
};
export default OnboardingTourSteps;